import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { useMediaQuery } from '@/hooks/hooks';
import { applyTheme, loadPrefs, savePrefs, type ThemePrefs } from '@/ui/theme';

interface ThemeContextValue {
  prefs: ThemePrefs;
  setPrefs: (p: ThemePrefs) => void;
}

const ThemeContext = createContext<ThemeContextValue>({ prefs: loadPrefs(), setPrefs: () => {} });

/** Applies the M3 color scheme and keeps it in sync with the OS dark mode. */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [prefs, setPrefsState] = useState(loadPrefs);
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');
  useEffect(() => applyTheme(prefs, prefersDark), [prefs, prefersDark]);
  const value = useMemo(
    () => ({
      prefs,
      setPrefs: (p: ThemePrefs) => {
        savePrefs(p);
        setPrefsState(p);
      },
    }),
    [prefs],
  );
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  return useContext(ThemeContext);
}
